/**
 * Names a code subsection in a link's tooltip, using the table of contents already on the page
 * (Issue #1773).
 *
 * The first tooltip this feature shipped read "Building code evidence / Building-code provision —
 * opens the canonical code section." for every link. That is true of all of them and therefore tells
 * a reader nothing about the one under the pointer: hovering `905.3.8` should say what 905.3.8 *is*.
 *
 * ## Where the title comes from, and why not from a request
 *
 * The compliance page has already loaded the book's table of contents in order to draw its section
 * tree, and each entry carries a title. This module turns that list into a lookup by section number,
 * which the report tab publishes once per load. A tooltip therefore costs a map read rather than a
 * network call — important because a report can name a dozen subsections, and a per-link request
 * would turn hovering into a fetch storm.
 *
 * ## Why the title is cleaned rather than shown as stored
 *
 * Catalog titles arrive decorated: `[F] 905.3.8 Rooftop Gardens and Landscaped Roofs.` carries a
 * source-code prefix, the number the tooltip is already about, and a trailing period that reads
 * badly mid-sentence. Stripping those is presentation, not interpretation — nothing is invented, and
 * when a title cannot be found the tooltip says `Section 905.3.8` and stops rather than guessing.
 */

/** A `[F]`, `[BS]`-style prefix the ICC catalog uses to mark the administering code. */
const BRACKETED_PREFIX = /^\s*\[[A-Za-z]{1,4}\]\s*/;

/** A section number at the start of a title, which the tooltip has already stated. */
const LEADING_SECTION_NUMBER = /^\s*[A-Za-z]?\d{3,5}(?:\.\d+)*\s*[-–—:.]?\s*/;

/** The section component of a fully qualified section ID, e.g. `_Sec905.3.8`. */
const SECTION_COMPONENT = /_Sec([0-9A-Za-z][0-9A-Za-z.]*)$/;

/** The bare section number inside a fully qualified section ID. Empty when it has none. */
export function sectionNumberOfId(sectionId: string): string {
  const match = SECTION_COMPONENT.exec(sectionId ?? '');
  return match ? match[1] : '';
}

/**
 * The readable part of a catalog title: no administering-code prefix, no repeated number, no
 * trailing period. Returns an empty string when nothing readable is left, so callers can treat
 * "no title" and "title that cleans away to nothing" the same way.
 */
export function cleanIccSectionTitle(rawTitle: string): string {
  return (
    (rawTitle ?? '')
      // Real catalog titles carry zero-width characters — 905.3.8 arrives as "Landscaped" followed by
      // seven U+200B and a non-breaking space before "Roofs". JavaScript's \s does NOT match U+200B,
      // so collapsing whitespace alone leaves them in and the tooltip renders a strange gap. Found by
      // reading the real book data rather than a fixture, which is the only place this was visible.
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(BRACKETED_PREFIX, '')
      .replace(LEADING_SECTION_NUMBER, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\.+$/, '')
      .trim()
  );
}

/**
 * Builds the number-to-title lookup from whatever section list the page already holds.
 *
 * Keyed by section NUMBER rather than by fully qualified ID because prose names numbers, and the
 * marker that reaches the renderer carries a number and nothing else.
 */
export function buildCodeSectionTitleIndex(
  sections: ReadonlyArray<{ sectionId?: string; title?: string }> | null | undefined
): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const section of sections ?? []) {
    const number = sectionNumberOfId(section?.sectionId ?? '');
    if (!number) continue;
    const title = cleanIccSectionTitle(section?.title ?? '');
    if (!title) continue;
    if (!index.has(number)) index.set(number, title);
  }
  return index;
}

/**
 * The first line of an inline code link's tooltip.
 *
 * With a title: `Section 905.3.8 — Rooftop Gardens and Landscaped Roofs`. Without one, and this is
 * the path taken whenever the number is absent from the loaded table of contents: `Section 905.3.8`.
 * Never a dangling dash, and never the word `undefined`.
 */
export function describeCodeSection(sectionNumber: string, title: string | undefined): string {
  const cleaned = cleanIccSectionTitle(title ?? '');
  return cleaned ? `Section ${sectionNumber} — ${cleaned}` : `Section ${sectionNumber}`;
}

/** The second line: what following the link does. Constant, because it is true of every one. */
export const CODE_LINK_ACTION = 'Opens the code text on ICC Digital Codes';

/**
 * Panel class that makes the two-line tooltip render as two lines.
 *
 * Material renders a tooltip in an overlay on the document body, outside the component that asked
 * for it, so component-scoped CSS cannot reach it and the newline collapses. The matching rule lives
 * in the global stylesheet; this constant is shared so the class name cannot drift apart from it.
 */
export const MULTILINE_TOOLTIP_CLASS = 'evidence-citation-tooltip--multiline';

/**
 * Merges title sources, preferring the fuller one and letting the other fill gaps.
 *
 * The book's table of contents is authoritative and complete to every depth; the page's compliance
 * tree is filtered but present sooner. Merging rather than choosing means a tooltip is never worse
 * than either source alone, and a report rendered before the contents arrive still names what it
 * can.
 */
export function mergeCodeSectionTitles(
  preferred: ReadonlyMap<string, string>,
  fallback: ReadonlyMap<string, string>
): ReadonlyMap<string, string> {
  const merged = new Map<string, string>(fallback);
  for (const [number, title] of preferred) merged.set(number, title);
  return merged;
}
